import { createContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { noAuthRequired } from "./ProtectRoutesContext";
import { useUser } from "./UserContext";
import { useStages } from "./StagesContext";
import { shouldShowPostAdvanceRegionalBriefing } from "@/utils/postAdvanceRegionalBriefing";
import { isPostAdvanceProfileComplete } from "@/utils/registrationProfile";
import { useBlockingDeadlineSnapshot } from "@/hooks/useBlockingDeadlineSnapshot";

type ProfileInfoContext = {};

const ProfileInfoContext = createContext<ProfileInfoContext>(
  {} as ProfileInfoContext
);

type ProfileInfoProviderProps = {
  children: React.ReactNode;
};

export const ProfileInfoProvider = ({ children }: ProfileInfoProviderProps) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { userInfo } = useUser();
  const {
    currentStageNumber,
    isLoading: isLoadingStages,
    isUserOutOfReality,
  } = useStages();

  const isAuthenticatedPage = !noAuthRequired.includes(pathname);

  const {
    inscription,
    isLoading: isInscriptionLoading,
  } = useBlockingDeadlineSnapshot({
    userId: userInfo?.id,
    enabled: isAuthenticatedPage && !!userInfo?.id,
    routeKey: pathname,
  });

  const advancedToResultStage = inscription?.advancedToResultStage === true;

  const hasPostAdvanceDataFilled = useMemo(
    () =>
      isPostAdvanceProfileComplete(
        userInfo,
        currentStageNumber,
        advancedToResultStage
      ),
    [userInfo, currentStageNumber, advancedToResultStage]
  );

  const isPostAdvanceRegionalBriefingOpen = useMemo(
    () =>
      shouldShowPostAdvanceRegionalBriefing({
        userId: userInfo?.id?.toString(),
        isStagesLoading: isLoadingStages,
        isAuthenticatedPage,
        isInscriptionLoading,
        hasInscription: !!inscription,
        isUserOutOfReality,
        inscriptionBlocksUi: false,
        currentStageNumber,
        advancedToResultStage,
        hasPostAdvanceProfileComplete: hasPostAdvanceDataFilled,
      }),
    [
      userInfo,
      isLoadingStages,
      isAuthenticatedPage,
      isInscriptionLoading,
      inscription,
      isUserOutOfReality,
      currentStageNumber,
      advancedToResultStage,
      hasPostAdvanceDataFilled,
    ]
  );

  const isBasicProfileComplete = useMemo(() => {
    if (!userInfo) {
      return true;
    }

    return !!(
      userInfo.email &&
      userInfo.name &&
      userInfo.phoneNumber &&
      userInfo.state &&
      userInfo.city &&
      userInfo.gender &&
      userInfo.birthDate
    );
  }, [{ ...userInfo }]);

  const isProfileStageComplete = useMemo(() => {
    if (!userInfo) {
      return true;
    }

    let isComplete = isBasicProfileComplete;

    if (currentStageNumber > 1) {
      isComplete =
        isComplete &&
        !!(
          userInfo.address &&
          userInfo.numberAddress &&
          userInfo.cep &&
          userInfo.shirtSize
        );
    }

    if (currentStageNumber > 2) {
      isComplete =
        isComplete &&
        !!(
          userInfo.cpf &&
          userInfo.phoneNumberResponsible &&
          userInfo.shoeSize &&
          userInfo.pantsSize
        );
    }

    return isComplete;
  }, [{ ...userInfo, currentStageNumber, isBasicProfileComplete }]);

  const isShowProfileRedirectRoute = useMemo(() => {
    if (pathname === "/profile" || noAuthRequired.includes(pathname)) {
      return false;
    }

    return true;
  }, [pathname]);

  /** Perfil básico incompleto: vai direto para /profile sem modal intermediário. */
  useEffect(() => {
    if (isPostAdvanceRegionalBriefingOpen) return;

    if (
      isProfileStageComplete ||
      !isShowProfileRedirectRoute ||
      !userInfo ||
      isLoadingStages ||
      isUserOutOfReality ||
      !isBasicProfileComplete
    ) {
      return;
    }

    navigate("/profile", { replace: true });
  }, [
    isLoadingStages,
    isBasicProfileComplete,
    isProfileStageComplete,
    pathname,
    userInfo,
    isShowProfileRedirectRoute,
    currentStageNumber,
    isUserOutOfReality,
    isPostAdvanceRegionalBriefingOpen,
    navigate,
  ]);

  return (
    <ProfileInfoContext.Provider value={{}}>
      {children}
    </ProfileInfoContext.Provider>
  );
};
